import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Tabs,
    Tab,
    CircularProgress,
    Alert,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import RedditTrackerChart from './RedditTrackerChart';
import RedditTrackerTable from './RedditTrackerTable';
import axios from '../../config/axiosConfig';

const SUBREDDIT_CONFIG = {
    wallstreetbets: {
        displayName: 'WallStreetBets',
        shortName: 'WSB',
        description: 'Track popular tickers from the WSB community known for high-risk, high-reward trading strategies'
    },
    valueinvesting: {
        displayName: 'Value Investing',
        shortName: 'Value',
        description: 'Monitor discussions around undervalued stocks and companies with strong fundamentals for long-term investment'
    },
    stocks: {
        displayName: 'Stocks',
        shortName: 'Stocks',
        description: 'General stock market conversations covering earnings, technical analysis, and investment opportunities'
    },
    investing: {
        displayName: 'Investing',
        shortName: 'Investing',
        description: 'Comprehensive investing discussions including stocks, bonds, ETFs, and portfolio management strategies'
    },
    stockmarket: {
        displayName: 'Stock Market',
        shortName: 'Market',
        description: 'Market-wide analysis tracking indices, economic indicators, and broader trading sentiment across sectors'
    }
};

const RedditTracker = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedSubreddit, setSelectedSubreddit] = useState('wallstreetbets');
    const [timePeriod, setTimePeriod] = useState('today');
    const [dataCache, setDataCache] = useState({}); // CHANGED: Cache data per subreddit
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableSubreddits, setAvailableSubreddits] = useState([]);

    // Fetch available subreddits
    useEffect(() => {
        const fetchSubreddits = async () => {
            try {
                const response = await axios.get('/api/reddit-tracker-subreddits');
                setAvailableSubreddits(response.data.subreddits || []);
            } catch (err) {
                console.error('Error fetching subreddits:', err);
            }
        };
        fetchSubreddits();
    }, []);

    // Fetch trending data with caching
    useEffect(() => {
        const fetchData = async () => {
            // Check if we already have cached data for this subreddit
            if (dataCache[selectedSubreddit]) {
                setLoading(false);
                return; // Use cached data, don't refetch
            }

            setLoading(true);
            setError(null);

            try {
                const response = await axios.get(`/api/reddit-tracker?subreddit=${selectedSubreddit}`);

                // Store data in cache
                setDataCache(prev => ({
                    ...prev,
                    [selectedSubreddit]: response.data
                }));
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to load data');
                console.error('Error fetching reddit tracker data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedSubreddit, dataCache]); // Added dataCache to dependencies

    const handleSubredditChange = (subreddit) => {
        setSelectedSubreddit(subreddit);
    };

    const handleTabChange = (event, newValue) => {
        setTimePeriod(newValue);
    };

    // Get current data from cache
    const data = dataCache[selectedSubreddit];
    const currentData = data?.[timePeriod] || [];
    const topStock = currentData[0];

    const timePeriodLabel = {
        today: 'today',
        oneWeek: 'the past week',
        oneMonth: 'the past month',
        // threeMonths: 'the past 3 months'
    }[timePeriod];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                    Reddit Stock Tracker
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {SUBREDDIT_CONFIG[selectedSubreddit]?.description}
                </Typography>
            </Box>

            {/* Subreddit Selector */}
            {availableSubreddits.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Select Subreddit
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {availableSubreddits.map((sub) => (
                            <Chip
                                key={sub.name}
                                label={`r/${sub.name}`}
                                onClick={() => handleSubredditChange(sub.name)}
                                color={selectedSubreddit === sub.name ? 'primary' : 'default'}
                                variant={selectedSubreddit === sub.name ? 'filled' : 'outlined'}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: selectedSubreddit === sub.name ? 600 : 400
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Time Period Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={timePeriod}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons={isMobile ? 'auto' : false}
                >
                    <Tab label="Today" value="today" />
                    <Tab label="Week" value="oneWeek" />
                    <Tab label="Month" value="oneMonth" />
                    {/* NEW Bring back on Jan 16  <Tab label="3 Months" value="threeMonths" /> */}
                </Tabs>
            </Box>

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Content */}
            {!loading && !error && data && (
                <>
                    {/* Overview Text */}
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Overview of r/{selectedSubreddit} most discussed stocks from {' '}
                        <strong>{timePeriodLabel}</strong>. The most discussed stock for this period is{' '}
                        <strong>{topStock?.symbol || 'n/a'}</strong>, representing{' '}
                        <strong>
                            {topStock?.weightPercentage
                                ? `${topStock.weightPercentage.toFixed(1)}%`
                                : 'n/a'}
                        </strong>{' '}
                        of total discussion volume.
                    </Typography>

                    {/* Chart - Hide on mobile */}
                    {currentData.length > 0 && (
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Card sx={{ mb: 4 }}>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                        Mentions Allocation
                                    </Typography>
                                    <RedditTrackerChart data={currentData} />
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* Table */}
                    {currentData.length > 0 && (
                        <RedditTrackerTable
                            data={currentData}
                            title={`${currentData.length} Stocks`}
                        />
                    )}

                    {/* No Data State */}
                    {currentData.length === 0 && (
                        <Alert severity="info">
                            No data available for this time period yet.
                        </Alert>
                    )}
                </>
            )}
        </Container>
    );
};

export default RedditTracker;